import React from 'react';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    LineElement,
    PointElement,
    Title,
    Tooltip,
    Legend
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import { SeasonData } from '../../types';
import { useTheme } from '../../contexts/ThemeContext';
import { chartTheme } from '../../theme/chartTheme';

ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend
);

type Props = {
    data: SeasonData[];
    managerId: string;
};

const ManagerPastPlacementsGraph: React.FC<Props> = ({ data, managerId }) => {
    const { theme } = useTheme();
    const palette = chartTheme(theme);
    const reversedData = [...data].reverse();
    const managerPoints = reversedData.map(season => {
        const player = season.players.find(p => p.id === Number(managerId));
        return player?.total_points ?? null;
    });

    const managerSeasonPlacements = reversedData.map(season => {
        const player = season.players.find(p => p.id === Number(managerId));
        return player ? Math.round(player.relative_rank) : null;
    });

    const seasonLabels = reversedData.map(season => season.season_name);

    const chartData = {
        labels: seasonLabels,
        datasets: [
            {
                label: 'Total Points per Season',
                data: managerPoints,
                fill: false,
                backgroundColor: palette.series1Fill,
                borderColor: palette.series1,
                tension: 0.2,
                pointRadius: 5,
                yAxisID: 'y',
            },
            {
                label: 'Season Placement',
                data: managerSeasonPlacements,
                fill: false,
                backgroundColor: palette.series2Fill,
                borderColor: palette.series2,
                tension: 0.2,
                pointRadius: 5,
                yAxisID: 'y1',
            },
        ]
    };

    const options = {
        responsive: true,
        plugins: {
            legend: {
                position: 'top' as const,
                labels: { color: palette.text }
            },
            title: {
                display: true,
                text: 'Manager Performance Over Seasons',
                color: palette.text
            }
        },
        scales: {
            x: {
                ticks: { color: palette.text },
                grid: { color: palette.grid }
            },
            y: {
                type: 'linear' as const,
                position: 'left' as const,
                ticks: { color: palette.text },
                grid: { color: palette.grid },
                title: {
                    display: true,
                    text: 'Points',
                    color: palette.text
                }
            },
            y1: {
                type: 'linear' as const,
                position: 'right' as const,
                reverse: true,
                ticks: { color: palette.text, stepSize: 1, },
                grid: {
                    drawOnChartArea: false
                },
                title: {
                    display: true,
                    text: 'Relative Rank',
                    color: palette.text
                }
            }
        }
    };

    return (
        <div className="mb-12 bg-surface-raised p-4 rounded-lg overflow-x-auto">
            <div className="min-w-[400px]">
                <Line data={chartData} options={options} />
            </div>
        </div>
    );
};

export default ManagerPastPlacementsGraph;
